import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Renderer2,
  SimpleChanges,
  inject
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';
import { ApiConstants } from 'src/app/base/api-constants';
import { AppGlobalService } from 'src/app/services/app.global.service';
import { getForeignKey, setForeignKey } from 'src/app/STORE/actions/datamodel.actions';
import { getFields, getParentFields, setFields, setParentFields } from 'src/app/STORE/actions/services.actions';
import { foreignKeyTbl } from 'src/app/STORE/selectors/datamodel.selector';
import { parentTableFields, tableFields } from 'src/app/STORE/selectors/services.selector';

@Component({
  selector: 'vb-transient-field-mapping',
  templateUrl: './transient-field-mapping.component.html',
  styleUrls: ['./transient-field-mapping.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TransientFieldMappingComponent),
      multi: true
    }
  ],
  standalone: false  //NOSONAR
})
export class TransientFieldMappingComponent implements OnInit, OnChanges, OnDestroy, ControlValueAccessor {
  private store = inject(Store);
  private renderer = inject(Renderer2);
  private elementRef = inject(ElementRef);
  private appGlobalService = inject(AppGlobalService);

  foreignKey: any;
  parentField: any;
  fieldsOptions: any = [];
  foreignKeyOptions: any = [];
  @Input() formData: any = null;
  @Input() formFields: any = null;
  @Input() element: any;
  foreignkeySub$ = this.store.select(foreignKeyTbl);
  destroy$: Subject<boolean> = new Subject<boolean>();
  @Input() tableIdMap: any = null;
  @Input() fieldIdMap: any = null;
  @Input() disableField = false;
  disabled = false;
  isLoading = false;
  private modelValue: any;
  protected onTouched: () => void;
  protected onChange: (value: any) => void;
  appId: any;
  orgId: any;

  get model() {
    return this.modelValue;
  }

  writeValue(value: any): void {
    this.modelValue = value;
    if (value) {
      try {
        const obj = JSON.parse(value);
        if (obj) {
          this.foreignKey = obj.f_fKeyId;
          this.parentField = obj.f_pTableFId;
          this.getOptions();
        }
      } catch (e) {
        console.error(e);
      }
    } else {
      this.foreignKey = null;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  set(value: any) {
    this.modelValue = value;
    this.onChange(this.modelValue);
  }

  setDisabledState(isDisabled: boolean) {
    this.renderer.setProperty(this.elementRef.nativeElement, 'disabled', isDisabled);
    this.disabled = isDisabled;
  }

  getOptions() {
    if (this.formData && this.formData.f_tableId) {
      if (
        this.formData.f_dataPopulationStrategy === 'reference' &&
        (!this.foreignKeyOptions || this.foreignKeyOptions.length === 0)
      ) {
        this.getForeignKeys();
      }
      if (this.parentField) {
        const field = this.fieldsOptions.find((fld) => fld.sid === this.parentField);
        if (this.formData.f_dataPopulationStrategy === 'reference' && !field) {
          const fk = this.foreignKeyOptions.filter((e) => e.sid === this.foreignKey);
          if (fk) {
            this.getFields(fk[0].parentTableSid);
          }
        }
      }
    }
  }
  ngOnChanges(changes: SimpleChanges): void {
    console.log(changes.formData);
    this.getOptions();
  }

  ngOnInit(): void {
    this.appId = this.appGlobalService.get('appId');
    this.orgId = this.appGlobalService.get('orgId');
    this.foreignkeySub$.pipe(takeUntil(this.destroy$)).subscribe((keys) => {
      if (keys) {
        this.isLoading = false;
        this.foreignKeyOptions = keys;
        if (this.foreignKey) {
          const selectedkey = this.foreignKeyOptions.filter((e) => e.sid === this.foreignKey);
          this.getFields(selectedkey[0].parentTableSid);
        }
      } else {
        this.foreignKeyOptions = [];
      }
    });
    this.store
      .select(parentTableFields)
      .pipe(takeUntil(this.destroy$))
      .subscribe((fields: any) => {
        if (fields) {
          this.isLoading = false;
          this.fieldsOptions = fields.filter((e) => e.f_type === this.formData.f_type);
        } else {
          this.fieldsOptions = [];
        }
      });
  }

  getForeignKeys() {
    if (!this.formData.f_tableId) {
      return;
    }
    const fkapiConstants = ApiConstants.getForeignKey;
    const data = { tableId: this.formData.f_tableId, orgId: this.orgId, appId: this.appId };
    this.isLoading = true;
    this.store.dispatch(
      getForeignKey({
        fkapiConstants,
        data
      })
    );
  }

  getFields(id) {
    if (id) {
      this.isLoading = true;
      if (this.fieldsOptions.length > 0 && this.fieldsOptions[0].tableId === id) {
        this.isLoading = false;
        return;
      }
      this.fieldsOptions = [];

      this.store.dispatch(
        getParentFields({
          parentAPiConstants: ApiConstants.getFieldInfo,
          params: { tableId: id }
        })
      );
    }
  }

  handleforeignKeyChange(event) {
    const fkId = event;
    const fk = this.foreignKeyOptions.filter((e) => e.sid === fkId);
    if (fk[0]) {
      this.getFields(fk[0].parentTableSid);
    }
    this.parentField = null;
  }

  handleFieldChange(event) {
    if (this.parentField && this.foreignKey) {
      this.set(JSON.stringify({ f_fKeyId: this.foreignKey, f_pTableFId: this.parentField }));
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
    if ($(this.elementRef.nativeElement)) {
      $(this.elementRef.nativeElement).remove();
    }
    this.store.dispatch(setParentFields({ parentFields: null }));
    this.store.dispatch(setForeignKey({ foreignKeyTbl: null }));
  }
}
